import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Row, Col, Select, Typography, Spin, message, Empty, Table, Input, Button,
  Tag, Tooltip, Space, Tabs, Card, Upload, Checkbox, Alert, Collapse, Statistic,
} from 'antd';
import {
  CheckOutlined, SaveOutlined, DownloadOutlined, CopyOutlined,
  CheckCircleFilled, InboxOutlined, CheckCircleOutlined, CloseCircleOutlined,
} from '@ant-design/icons';
import { eventoService, videoService } from '../../services/api';
import { eventosDeListaCorta } from '../../utils/listaCorta';

const { Text } = Typography;
const { Option } = Select;
const { Dragger } = Upload;

// Mismo criterio que el backend (validar_video_url): YouTube-only por ahora.
const YT_RE = /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([\w-]{6,})/i;
const urlValida = (u) => !u || YT_RE.test(u.trim());

// ── Grid pre-cargado por evento ───────────────────────────────────────────────
const GridVideos = ({ eventoId, loading, filas, setFilas }) => {
  const [savingId, setSavingId] = useState(null);
  const [savingCorral, setSavingCorral] = useState(null);

  const setUrl = (id, url) =>
    setFilas((prev) => ({ ...prev, [id]: { ...prev[id], url, dirty: true } }));

  // Guarda una lista de {id_animal, url} en un solo request (bulk).
  const guardarItems = async (rows, corral = null) => {
    const items = rows.map((r) => ({ id_animal: r.id_animal, codigo_video: (r.url || '').trim() }));
    if (corral != null) setSavingCorral(corral); else setSavingId(rows[0]?.id_animal);
    try {
      const { data } = await videoService.bulk(items);
      const errById = {};
      (data.errores || []).forEach((e) => { errById[e.id_animal] = e.error; });
      setFilas((prev) => {
        const next = { ...prev };
        rows.forEach((r) => {
          if (errById[r.id_animal]) return;                    // dejó error → no marcar guardado
          next[r.id_animal] = { ...next[r.id_animal], codigo_video: (r.url || '').trim() || null, dirty: false };
        });
        return next;
      });
      if (data.actualizados) message.success(`${data.actualizados} video(s) guardado(s)`);
      (data.errores || []).forEach((e) => {
        const rp = filas[e.id_animal]?.rp_animal || e.id_animal;
        message.error(`RP ${rp}: ${e.error}`);
      });
    } catch (err) {
      message.error(err?.response?.data?.error || 'Error al guardar');
    } finally {
      setSavingId(null); setSavingCorral(null);
    }
  };

  const guardarFila = (r) => {
    if (!urlValida(r.url)) { message.warning('URL de YouTube inválida'); return; }
    guardarItems([r]);
  };

  // Copia la URL de este animal al resto del corral y guarda todo el lote.
  const aplicarCorral = (r) => {
    if (!urlValida(r.url)) { message.warning('URL de YouTube inválida'); return; }
    const lote = Object.values(filas).filter((f) => f.agrupado && f.corral === r.corral);
    const conUrl = lote.map((f) => ({ ...f, url: r.url }));
    setFilas((prev) => {
      const next = { ...prev };
      conUrl.forEach((f) => { next[f.id_animal] = { ...next[f.id_animal], url: r.url, dirty: true }; });
      return next;
    });
    guardarItems(conUrl, r.corral);
  };

  const corralCount = useMemo(() => {
    const m = {};
    Object.values(filas).forEach((f) => { if (f.agrupado && f.corral != null) m[f.corral] = (m[f.corral] || 0) + 1; });
    return m;
  }, [filas]);

  const dataSource = useMemo(
    () => Object.values(filas).sort((a, b) => {
      const ca = a.corral ?? Infinity, cb = b.corral ?? Infinity;
      if (ca !== cb) return ca - cb;
      return String(a.rp_animal || '').localeCompare(String(b.rp_animal || ''));
    }),
    [filas],
  );

  const conVideo = useMemo(() => Object.values(filas).filter((f) => f.codigo_video).length, [filas]);
  const total = Object.keys(filas).length;

  // Pegar desde Excel: cae POR POSICIÓN desde la fila anclada hacia abajo, en el
  // orden del grid (corral→RP). Toma la última celda no vacía de cada línea, así
  // funciona tanto si pegan solo la columna URL como si pegan filas completas.
  const handlePaste = (e, startIndex) => {
    const text = e.clipboardData.getData('text');
    if (!text || !text.includes('\n')) return;   // un solo valor → lo maneja el Input
    e.preventDefault();
    const lineas = text.replace(/\r/g, '').split('\n');
    while (lineas.length && lineas[lineas.length - 1] === '') lineas.pop();  // newline final
    // Armo los updates FUERA del updater (pure). Celda en blanco → NO pisa el
    // video que ya tiene ese animal (evita borrado silencioso al pegar columnas
    // con huecos).
    const updates = [];
    lineas.forEach((linea, i) => {
      const row = dataSource[startIndex + i];
      if (!row) return;
      const celdas = linea.split('\t').map((c) => c.trim());
      const url = [...celdas].reverse().find((c) => c) || '';   // última celda no vacía
      if (!url) return;
      updates.push([row.id_animal, url]);
    });
    setFilas((prev) => {
      const next = { ...prev };
      updates.forEach(([id, url]) => { next[id] = { ...next[id], url, dirty: true }; });
      return next;
    });
    if (updates.length) message.success(`${updates.length} URL(s) pegada(s) — revisá y guardá`);
  };

  // Descarga la planilla EN EL ORDEN DEL GRID (corral→RP): el usuario llena la
  // columna URL en su lugar y la pega → alineación garantizada. Mismo formato que
  // acepta la herramienta CSV (CORRAL;RP;URL, la columna ANIMAL se ignora).
  const descargarPlantilla = () => {
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const filasCsv = dataSource.map((r) => [r.corral ?? '', r.rp_animal ?? '', r.nom_animal ?? '', ''].map(esc).join(';'));
    const csv = ['CORRAL;RP;ANIMAL;URL', ...filasCsv].join('\r\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'planilla_videos.csv';
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };

  const columns = [
    { title: 'Corral', dataIndex: 'corral', width: 72, align: 'center', render: (v) => v ?? '—' },
    {
      title: 'RP', dataIndex: 'rp_animal', width: 96,
      render: (v, r) => (
        <Space size={4}>
          {r.codigo_video && <CheckCircleFilled style={{ color: '#52c41a' }} />}
          <span>{v}</span>
        </Space>
      ),
    },
    {
      title: 'Animal', dataIndex: 'nom_animal',
      render: (v, r) => (
        <Space size={4}>
          <span>{v}</span>
          {r.codigo_video && <Tag color="green" style={{ margin: 0 }}>Ya tiene video</Tag>}
        </Space>
      ),
    },
    {
      title: 'URL del video (YouTube)', dataIndex: 'url', width: 320,
      render: (v, r, index) => {
        const err = !urlValida(v);
        return (
          <Input
            size="small" placeholder="https://youtu.be/..." value={v}
            status={err ? 'error' : ''}
            onChange={(e) => setUrl(r.id_animal, e.target.value)}
            onPaste={(e) => handlePaste(e, index)}
          />
        );
      },
    },
    {
      title: '', key: 'accion', width: 96, align: 'center',
      render: (_, r) => {
        const enGrupo = r.agrupado && corralCount[r.corral] > 1;
        return (
          <Space size={2}>
            <Tooltip title={r.codigo_video ? 'Actualizar' : 'Guardar'}>
              <Button
                type={r.codigo_video ? 'default' : 'primary'} size="small"
                icon={r.codigo_video ? <SaveOutlined /> : <CheckOutlined />}
                loading={savingId === r.id_animal}
                disabled={!r.dirty || !urlValida(r.url)}
                onClick={() => guardarFila(r)}
              />
            </Tooltip>
            {enGrupo && (
              <Tooltip title="Aplicar esta URL a todo el corral y guardar">
                <Button size="small" icon={<CopyOutlined />} loading={savingCorral === r.corral}
                  disabled={!(r.url || '').trim() || !urlValida(r.url)} onClick={() => aplicarCorral(r)} />
              </Tooltip>
            )}
          </Space>
        );
      },
    },
  ];

  if (loading) return <div style={{ textAlign: 'center', padding: 48 }}><Spin size="large" /></div>;
  if (!eventoId) return <Empty description="Seleccioná un evento para cargar los videos" />;
  if (total === 0) return <Empty description="No hay animales en este evento" />;

  return (
    <>
      <Alert
        type="info" showIcon closable banner style={{ marginBottom: 8, padding: '4px 12px' }}
        message={
          <Text style={{ fontSize: 12 }}>
            Pegá la columna de URLs en la 1ª celda de video (cae por posición). Alineá con la <strong>planilla descargada</strong> o ordenando por Corral y RP.
          </Text>
        }
      />
      <Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
        <Col>
          <Button size="small" icon={<DownloadOutlined />} onClick={descargarPlantilla}>
            Descargar planilla
          </Button>
        </Col>
        <Col><Text type="secondary">{conVideo} / {total} con video</Text></Col>
      </Row>
      <Table
        dataSource={dataSource}
        columns={columns}
        rowKey="id_animal"
        size="small"
        pagination={false}
        scroll={{ x: 'max-content', y: 460 }}
      />
    </>
  );
};

// ── Herramienta CSV ───────────────────────────────────────────────────────────
const CargaCSV = ({ eventoId, onCargado }) => {
  const [file, setFile] = useState(null);
  const [sobrescribir, setSobrescribir] = useState(false);
  const [validando, setValidando] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [resultado, setResultado] = useState(null);

  const uploadProps = {
    accept: '.csv', maxCount: 1, fileList: file ? [file] : [],
    beforeUpload: (f) => {
      if (!f.name.toLowerCase().endsWith('.csv')) { message.error('Debe ser un archivo .csv'); return Upload.LIST_IGNORE; }
      setFile(f); setResultado(null); return false;
    },
    onRemove: () => { setFile(null); setResultado(null); },
  };

  const run = async (accion) => {
    if (!eventoId) { message.warning('Seleccioná un evento'); return; }
    if (!file) { message.warning('Elegí un archivo CSV'); return; }
    const setLoad = accion === 'validar' ? setValidando : setCargando;
    setLoad(true);
    try {
      const svc = accion === 'validar' ? videoService.csvValidar : videoService.csvCargar;
      const { data } = await svc(file, eventoId, sobrescribir);
      setResultado({ ...data, accion });
      if (accion === 'cargar') {
        message.success(`${data.aplicados ?? 0} video(s) aplicado(s)`);
        onCargado?.();
      }
    } catch (err) {
      message.error(err?.response?.data?.error || 'Error al procesar el CSV');
    } finally {
      setLoad(false);
    }
  };

  return (
    <div>
      <Alert
        type="info" showIcon style={{ marginBottom: 16 }}
        message="Formato del CSV (separador ;)"
        description={
          <div>
            Columnas: <Tag>CORRAL</Tag><Tag>RP</Tag><Tag>URL</Tag>. Por fila: si ponés <strong>RP</strong> matchea ese animal;
            si ponés solo <strong>CORRAL</strong>, el video se aplica a <strong>todo el lote</strong> de ese corral. Solo URLs de YouTube.
          </div>
        }
      />
      <Dragger {...uploadProps} style={{ marginBottom: 16 }}>
        <p className="ant-upload-drag-icon"><InboxOutlined /></p>
        <p className="ant-upload-text">Hacé clic o arrastrá el CSV acá</p>
      </Dragger>
      <Space style={{ marginBottom: 16 }} wrap>
        <Checkbox checked={sobrescribir} onChange={(e) => setSobrescribir(e.target.checked)}>
          Sobrescribir videos ya cargados
        </Checkbox>
        <Button loading={validando} disabled={!file} onClick={() => run('validar')}>Validar</Button>
        <Button type="primary" loading={cargando} disabled={!file} onClick={() => run('cargar')}>Cargar</Button>
      </Space>

      {resultado && (
        <Card size="small" title={`Resultado (${resultado.accion})`}>
          <Row gutter={16} style={{ marginBottom: 12 }}>
            <Col xs={24} sm={12} md={8}><Statistic title={resultado.accion === 'cargar' ? 'Aplicados' : 'Válidos'}
              value={resultado.accion === 'cargar' ? (resultado.aplicados ?? 0) : (resultado.validos?.length ?? 0)}
              valueStyle={{ color: '#52c41a' }} prefix={<CheckCircleOutlined />} /></Col>
            <Col xs={24} sm={12} md={8}><Statistic title="Omitidos (ya tenían)" value={resultado.omitidos?.length ?? 0}
              valueStyle={{ color: '#faad14' }} /></Col>
            <Col xs={24} sm={12} md={8}><Statistic title="Errores" value={resultado.errores?.length ?? 0}
              valueStyle={{ color: '#ff4d4f' }} prefix={<CloseCircleOutlined />} /></Col>
          </Row>
          <Collapse ghost items={[
            resultado.errores?.length && {
              key: 'err', label: <Text type="danger">Ver errores ({resultado.errores.length})</Text>,
              children: <Table scroll={{ x: 'max-content' }} size="small" pagination={false} rowKey={(r) => `${r.fila}-${r.detalle}`}
                dataSource={resultado.errores}
                columns={[{ title: 'Fila', dataIndex: 'fila', width: 70 }, { title: 'Detalle', dataIndex: 'detalle' }]} />,
            },
            resultado.omitidos?.length && {
              key: 'omi', label: <Text type="warning">Ver omitidos ({resultado.omitidos.length})</Text>,
              children: <Table scroll={{ x: 'max-content' }} size="small" pagination={false} rowKey={(r) => `${r.fila}-${r.rp_animal}`}
                dataSource={resultado.omitidos}
                columns={[{ title: 'Fila', dataIndex: 'fila', width: 70 }, { title: 'RP', dataIndex: 'rp_animal', width: 100 }, { title: 'Detalle', dataIndex: 'detalle' }]} />,
            },
          ].filter(Boolean)} />
        </Card>
      )}
    </div>
  );
};

// ── Tab de Videos (selector de evento + 2 herramientas) ───────────────────────
const VideosTab = () => {
  const [eventos, setEventos] = useState([]);
  const [loadingEventos, setLoadingEventos] = useState(true);
  const [eventoId, setEventoId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filas, setFilas] = useState({});

  useEffect(() => {
    (async () => {
      setLoadingEventos(true);
      try {
        const res = await eventoService.getListaCorta();
        // Antes habia un `|| res.data` por si el endpoint devolvia la lista
        // pelada. Nunca lo hizo: devuelve SIEMPRE `{eventos, total}`.
        setEventos(eventosDeListaCorta(res));
      } catch (err) { message.error(err?.response?.data?.error || 'Error al cargar eventos'); }
      finally { setLoadingEventos(false); }
    })();
  }, []);

  const precargar = useCallback(async () => {
    if (!eventoId) { setFilas({}); return; }
    setLoading(true);
    try {
      const res = await videoService.precargaEvento(eventoId);
      const map = {};
      (res.data?.animales || []).forEach((a) => {
        map[a.id_animal] = { ...a, url: a.codigo_video || '', dirty: false };
      });
      setFilas(map);
    } catch (err) { message.error(err?.response?.data?.error || 'Error al cargar los videos del evento'); }
    finally { setLoading(false); }
  }, [eventoId]);

  useEffect(() => { precargar(); }, [precargar]);

  return (
    <div>
      <Row align="middle" gutter={12} style={{ marginBottom: 16 }}>
        <Col flex="auto">
          <Select
            loading={loadingEventos} placeholder="Seleccioná un evento..."
            value={eventoId} onChange={setEventoId} style={{ minWidth: 320 }} allowClear
          >
            {eventos.map((e) => <Option key={e.id_evento} value={e.id_evento}>{e.nombre_evento}</Option>)}
          </Select>
        </Col>
      </Row>

      <Tabs
        items={[
          {
            key: 'planilla', label: 'Planilla en pantalla',
            children: <GridVideos eventoId={eventoId} loading={loading} filas={filas} setFilas={setFilas} />,
          },
          {
            key: 'csv', label: 'Carga por CSV',
            children: <CargaCSV eventoId={eventoId} onCargado={precargar} />,
          },
        ]}
      />
    </div>
  );
};

export default VideosTab;
